import { useCallback, useEffect, useRef, useState } from 'react';

import { commitOwnerKey, generateOwnerKey, wrapBridgeKey } from '@/crypto';
import { transport } from '@/api/transport';
import { ApiError, type NearbyEvent, type NearbyPeer } from '@/api/types';
import type { NearbyState } from '@/components/Nearby';
import { sasEmoji } from '@/lib/nearby';
import { IDLE, isLive, step, type OwnerEffect, type OwnerInput, type OwnerState } from '@/lib/nearbyOwner';
import { useLatest } from '@/lib/useLatest';

export interface NearbySend {
  readonly step: OwnerState;
  readonly send: (peer: NearbyPeer) => void;
  readonly confirm: () => void;
  readonly cancel: () => void;
}

interface Context {
  readonly token: string | null;
  readonly bridgeId: string;
  readonly bridgeKey: string;
  readonly ownerToken: string;
  readonly onSent: () => void;
}

const reasonOf = (error: unknown): 'view_limit' | undefined => (error instanceof ApiError && error.code === 'view_limit' ? 'view_limit' : undefined);
const messageOf = (error: unknown): string | undefined => (error instanceof Error ? error.message : undefined);

/** Carries out one effect; what comes of it goes back in as an input. */
function perform(effect: OwnerEffect, context: Context, feed: (input: OwnerInput) => void): void {
  const { token } = context;
  if (token === null) return;
  switch (effect.type) {
    case 'offer':
      void (async () => {
        try {
          const ownerKeyHash = await commitOwnerKey('nearby', effect.pair.publicKey);
          const { offerId } = await transport.nearbyOffer(token, { to: effect.peer.id, bridgeId: context.bridgeId, ownerToken: context.ownerToken, ownerKeyHash });
          feed({ type: 'offered', offerId });
        } catch (error) {
          feed({ type: 'failed', offerId: null, reason: reasonOf(error), message: messageOf(error) });
        }
      })();
      return;
    case 'reveal':
      void (async () => {
        try {
          await transport.nearbyReveal({ token, offerId: effect.offerId, ownerPublicKey: effect.pair.publicKey });
          feed({ type: 'revealed', offerId: effect.offerId, emoji: await sasEmoji('nearby', effect.pair.publicKey, effect.guestPublicKey) });
        } catch (error) {
          feed({ type: 'failed', offerId: effect.offerId, message: messageOf(error) });
        }
      })();
      return;
    case 'release':
      void (async () => {
        try {
          const wrappedKey = await wrapBridgeKey(effect.pair, effect.guestPublicKey, context.bridgeKey, { flow: 'nearby', id: effect.offerId });
          await transport.nearbyKey({ token, offerId: effect.offerId, ownerToken: context.ownerToken, wrappedKey });
          feed({ type: 'sent', offerId: effect.offerId });
          context.onSent();
        } catch (error) {
          feed({ type: 'failed', offerId: effect.offerId, reason: reasonOf(error), message: messageOf(error) });
        }
      })();
      return;
    case 'decline':
      void transport.nearbyDecline({ token, offerId: effect.offerId }).catch(() => undefined);
  }
}

/**
 * Share → Nearby, the sending side: runs `lib/nearbyOwner.ts` against the stream and the transport. The state also
 * lives in a ref, so two events in a row see each other's result.
 */
export function useNearbySend(nearby: NearbyState | null, bridgeId: string, bridgeKey: string, ownerToken: string, onSent: () => void): NearbySend {
  const token = nearby?.token ?? null;
  const [view, setView] = useState<OwnerState>(IDLE);
  const state = useRef<OwnerState>(IDLE);
  const context = useLatest<Context>({ token, bridgeId, bridgeKey, ownerToken, onSent });

  const [feed] = useState(() => {
    const run = (input: OwnerInput): void => {
      const next = step(state.current, input);
      state.current = next.state;
      setView(next.state);
      for (const effect of next.effects) perform(effect, context.current, run);
    };
    return run;
  });

  // The other device's steps: its public key, its yes, or the end of the offer.
  const listen = nearby?.listen;
  useEffect(() => {
    if (!listen) return undefined;
    return listen((event: NearbyEvent) => {
      if (event.type === 'answer' || event.type === 'accepted' || event.type === 'closed') feed({ type: 'peer', event });
    });
  }, [listen, feed]);

  // Leaving the tab mid-offer calls it off.
  useEffect(
    () => () => {
      if (isLive(state.current)) feed({ type: 'cancel' });
    },
    [feed],
  );

  const send = useCallback(
    (peer: NearbyPeer): void => {
      if (context.current.token === null) return;
      void generateOwnerKey().then((pair) => {
        feed({ type: 'send', peer, pair });
      });
    },
    [context, feed],
  );
  const confirm = useCallback(() => {
    feed({ type: 'confirm' });
  }, [feed]);
  const cancel = useCallback(() => {
    feed({ type: 'cancel' });
  }, [feed]);

  return { step: view, send, confirm, cancel };
}
